// import { useEffect, useState } from "react";
// import AdminListToolkit from "../../components/listing/AdminListToolkit";
// import DataTable from "../../components/DataTable";
// import GeneralTradeDocumentForm from "../../components/generalTrade/GeneralTradeDocumentForm";
// import { gtApi } from "../../api/generalTradeApi";
// import { useFormat } from "../../../context/FormatContext";

// export default function SalesOrders() {
//   const [rows, setRows] = useState<any[]>([]),
//     [selected, setSelected] = useState<any>(null),
//     [adding, setAdding] = useState(false);

//   const { formatDateToDMY, formatDateOnly } = useFormat();

//   const load = async (): Promise<void> => {
//     try {
//       const salesOrders = await gtApi.salesOrders();

//       setRows(Array.isArray(salesOrders) ? salesOrders : []);
//     } catch (error) {
//       console.error("Failed to load Sales Orders:", error);
//       setRows([]);
//     }
//   };

//   const columns = [
//     { key: "sales_order_number", label: "SO No." },
//     {
//       key: "order_date",
//       label: "Order Date",
//       render: (row: any) =>
//         row.order_date ? formatDateOnly(row.order_date) : "-",
//     },
//     { key: "company_name", label: "Customer" },
//     { key: "grand_total", label: "Amount" },
//     {
//       key: "created_at",
//       label: "Created At",
//       render: (row: any) =>
//         row.created_at ? formatDateToDMY(row.created_at) : "-",
//     },
//     {
//       key: "updated_at",
//       label: "Updated At",
//       render: (row: any) =>
//         row.updated_at ? formatDateToDMY(row.updated_at) : "-",
//     },
//     { key: "status", label: "Status" },
//   ];

//   useEffect(() => {
//     void load();
//   }, []);
//   if (adding)
//     return (
//       <GeneralTradeDocumentForm
//         onCancel={() => setAdding(false)}
//         onSaved={() => {
//           setAdding(false);
//           load();
//         }}
//       />
//     );
//   return (
//     <AdminListToolkit
//       title="SALES ORDERS"
//       columns={columns}
//       data={rows}
//       selectedRow={selected}
//       onAdd={() => setAdding(true)}
//       onView={() =>
//         window.open(gtApi.pdfUrl("sales-orders", selected.id), "_blank")
//       }
//       enableExport
//       exportFileName="sales-orders"
//       extraButtons={[
//         {
//           label: "Share",
//           onClick: async () => {
//             const url = gtApi.pdfUrl("sales-orders", selected.id);
//             if (navigator.share)
//               await navigator.share({
//                 title: selected.sales_order_number,
//                 url,
//               });
//             else await navigator.clipboard.writeText(url);
//           },
//         },
//       ]}
//     >
//       {(filtered) => (
//         <DataTable
//           columns={columns}
//           data={filtered}
//           selectedRowId={selected?.id}
//           onRowSelect={setSelected}
//         />
//       )}
//     </AdminListToolkit>
//   );
// }

import { useEffect, useState } from "react";
import AdminListToolkit from "../../components/listing/AdminListToolkit";
import DataTable from "../../components/DataTable";
import GeneralTradeDocumentForm from "../../components/generalTrade/GeneralTradeDocumentForm";
import { gtApi } from "../../api/generalTradeApi";
import { useFormat } from "../../../context/FormatContext";
import { useMessageModal } from "../../contexts/MessageModalContext";

type SalesOrderScreenMode = "list" | "add" | "edit" | "view";

interface SalesOrderRow {
  id: number;
  sales_order_number: string;
  order_date: string;
  company_name: string;
  grand_total: number | string;
  created_at: string;
  updated_at: string;
  status: string;

  is_locked?: number | boolean;
  is_editable?: number | boolean;

  active_delivery_order_id?: number | null;
  active_delivery_order_number?: string | null;
  active_delivery_order_count?: number;
  total_delivery_order_count?: number;
}

export default function SalesOrders() {
  const { formatDateToDMY, formatDateOnly, formatCurrency } = useFormat();
  const { showModal } = useMessageModal();

  const [rows, setRows] = useState<SalesOrderRow[]>([]);
  const [selected, setSelected] = useState<SalesOrderRow | null>(null);

  const [screenMode, setScreenMode] = useState<SalesOrderScreenMode>("list");

  const [activeSalesOrderId, setActiveSalesOrderId] = useState<number | null>(
    null,
  );

  const load = async (): Promise<void> => {
    try {
      const salesOrders = await gtApi.salesOrders();

      const safeRows = Array.isArray(salesOrders) ? salesOrders : [];

      setRows(safeRows);

      setSelected((previous) => {
        if (!previous) return null;

        return (
          safeRows.find(
            (row: SalesOrderRow) => Number(row.id) === Number(previous.id),
          ) || null
        );
      });
    } catch (error) {
      console.error("Failed to load Sales Orders:", error);
      setRows([]);
      setSelected(null);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const openAdd = () => {
    setActiveSalesOrderId(null);
    setScreenMode("add");
  };

  const openView = (row: SalesOrderRow) => {
    setActiveSalesOrderId(Number(row.id));
    setScreenMode("view");
  };

  const openEdit = (row: SalesOrderRow) => {
    const isLocked =
      Number(row.is_locked || 0) === 1 || Number(row.is_editable ?? 1) === 0;

    if (isLocked) {
      showModal({
        type: "error",
        title: "Sales Order View Only",
        message: row.active_delivery_order_number
          ? `This Sales Order is linked to active Delivery Order ${row.active_delivery_order_number} and cannot be edited.`
          : "This Sales Order is linked to an active Delivery Order and cannot be edited.",
        confirmText: "OK",
      });

      return;
    }

    setActiveSalesOrderId(Number(row.id));
    setScreenMode("edit");
  };

  const returnToList = () => {
    setScreenMode("list");
    setActiveSalesOrderId(null);
  };

  const handleSaved = () => {
    returnToList();
    setSelected(null);
    void load();
  };

  const columns = [
    {
      key: "sales_order_number",
      label: "SO No.",
    },
    {
      key: "order_date",
      label: "Order Date",
      render: (row: SalesOrderRow) =>
        row.order_date ? formatDateOnly(row.order_date) : "-",
    },
    {
      key: "company_name",
      label: "Customer",
    },
    {
      key: "grand_total",
      label: "Amount",
      render: (row: SalesOrderRow) =>
        formatCurrency(Number(row.grand_total || 0)),
    },
    {
      key: "active_delivery_order_number",
      label: "Active DO",
      render: (row: SalesOrderRow) => row.active_delivery_order_number || "-",
    },
    {
      key: "edit_status",
      label: "Edit Status",
      render: (row: SalesOrderRow) => {
        const locked =
          Number(row.is_locked || 0) === 1 ||
          Number(row.is_editable ?? 1) === 0;

        return (
          <span className={locked ? "badge-danger" : "badge-success"}>
            {locked ? "View Only" : "Editable"}
          </span>
        );
      },
    },
    {
      key: "created_at",
      label: "Created At",
      render: (row: SalesOrderRow) =>
        row.created_at ? formatDateToDMY(row.created_at) : "-",
    },
    {
      key: "updated_at",
      label: "Updated At",
      render: (row: SalesOrderRow) =>
        row.updated_at ? formatDateToDMY(row.updated_at) : "-",
    },
    {
      key: "status",
      label: "Status",
      render: (row: SalesOrderRow) => (
        <span
          className={
            row.status === "submitted"
              ? "badge-success"
              : row.status === "cancelled"
                ? "badge-danger"
                : "badge-warning"
          }
        >
          {String(row.status || "")
            .charAt(0)
            .toUpperCase() + String(row.status || "").slice(1)}
        </span>
      ),
    },
  ];

  if (screenMode !== "list") {
    return (
      <GeneralTradeDocumentForm
        mode={screenMode}
        salesOrderId={activeSalesOrderId}
        onCancel={returnToList}
        onSaved={handleSaved}
      />
    );
  }

  return (
    <AdminListToolkit
      title="SALES ORDERS"
      columns={columns}
      data={rows}
      selectedRow={selected}
      onAdd={openAdd}
      onView={(row) => openView(row)}
      onEdit={(row) => openEdit(row)}
      enableExport
      exportFileName="sales-orders"
      extraButtons={[
        {
          label: "PDF",
          onClick: () => {
            if (!selected) return;

            window.open(gtApi.pdfUrl("sales-orders", selected.id), "_blank");
          },
        },
        {
          label: "Share",
          onClick: async () => {
            if (!selected) return;

            const url = gtApi.pdfUrl("sales-orders", selected.id);

            if (navigator.share) {
              await navigator.share({
                title: selected.sales_order_number,
                url,
              });
            } else {
              await navigator.clipboard.writeText(url);

              showModal({
                type: "success",
                title: "Link Copied",
                message: "Sales Order PDF link copied to clipboard.",
                confirmText: "OK",
              });
            }
          },
        },
      ]}
    >
      {(filteredRows) => (
        <DataTable
          columns={columns}
          data={filteredRows}
          selectedRowId={selected?.id}
          onRowSelect={setSelected}
        />
      )}
    </AdminListToolkit>
  );
}
